import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { ModalService } from '../../services/modal.service';

import { Observable, forkJoin } from 'rxjs';
import { I18nTranslator } from '../../i18n/translate';
import { I18nInfo } from '../../models/i18n.model';
import { Country, Language } from '../../models/lists.model';
import { Organisation } from '../../models/organisation.model';
import { Role } from '../../models/role.model';
import { User } from '../../models/user.model';
import { CapitalizePipe } from '../../pipes/capitalize.pipe';
import { I18nService } from '../../services/i18n.service';
import { KeycloakService } from '../../services/keycloak.service';
import { ListsService } from '../../services/lists.service';
import { OrganisationService } from '../../services/organisation.service';
import { RoleService } from '../../services/role.service';
import { UserService } from '../../services/user.service';
import { showErrorMessage, sortingString } from '../../utils/utils';
import * as _ from 'lodash';
import { TranslatePipe, TranslateWithPunctuationPipe } from '../../pipes/translate.pipe';
import { CommonModule } from '@angular/common';

@Component({
  standalone: true,
  selector: 'user-edit',
  templateUrl: 'user-edit.component.html',
  imports: [RouterLink, RouterLinkActive, RouterOutlet, FormsModule, CapitalizePipe, TranslatePipe, TranslateWithPunctuationPipe, CommonModule],
  providers: [UserService, OrganisationService],
})
export class UserEditComponent implements OnInit, OnDestroy {
  @Input() user: User;
  @Output() close = new EventEmitter();
  error: any;
  sub: any;

  edited = false;
  countries: Country[];
  organisations: Organisation[];
  filteredOrganisations: Organisation[] = [];
  showOrgaDropdown: boolean = false;
  organisationInput: string = '';
  np1: User[] = [];
  advisors: User[];
  managers: User[];
  filteredAdvisors: User[] = [];
  showAdvisorDropdown: boolean = false;
  advisorInput: string = '';
  userId: number;

  allRoles: Role[];
  rolesWanaka: Role[];
  rolesOrg: Role[];
  roleWanaka: Role;
  roleOrg: Role;
  isAdmin: boolean;
  isSector: boolean;
  isUserMe: boolean;
  isAdvisorDisabled = true;
  isOrgDisabled = true;
  indicatif: Country[];
  prefixPhone: number = null;
  phone: string;
  prefixMobile: number = null;
  mobile: string;

  languagesIndex: Map<number, Language>;
  organisationIndex: Map<number, Organisation>;
  countryIndex: Map<number, Country>;
  indicatifIndex: Map<string, Country>;
  organisationCountry: Country;

  roleFarmerId: number;

  managerLabel: string;

  i18nInfo: I18nInfo;

  constructor(
    private userService: UserService,
    private roleService: RoleService,
    private listService: ListsService,
    private organisationService: OrganisationService,
    private i18nTranslator: I18nTranslator,
    private i18nService: I18nService,
    private modal: ModalService,
    private router: Router,
    private route: ActivatedRoute,
  ) {}

  goBack(savedUser: User = null) {
    this.close.emit(savedUser);
    this.router.navigate(['/users']);
    return false;
  }

  handleCountries(countries: Country[]) {
    this.countries = countries;
    this.countries = countries.sort(sortingString('label'));

    this.countryIndex = new Map<number, Country>();
    for (const country of countries) {
      this.countryIndex.set(country.id, country);
    }

    this.indicatif = [];
    this.indicatifIndex = new Map<string, Country>();
    for (const country of countries) {
      this.indicatif.push(country);
      this.indicatifIndex.set(country.telCountryCode, country);
    }
    this.indicatif.sort(sortingString('telCountryCode'));
  }

  formatIndicatif(country: Country): string {
    return country.telCountryCode + ' - ' + country.label;
  }

  handleLanguages(languages: Language[]) {
    this.languagesIndex = new Map<number, Language>();
    for (const language of languages) {
      this.languagesIndex.set(language.id, language);
    }
  }

  handleRoles(user: User, allRoles: Role[]) {
    // on requête les rôels pour avoir les id
    this.allRoles = allRoles;

    this.rolesWanaka = [];
    this.rolesOrg = [];
    let setRoles: Set<string> = null;
    if (this.edited) {
      const roles: string[] = user.roles.map((r) => r.code);
      setRoles = new Set<string>(roles);
    }
    for (const role of allRoles) {
      if (role.code === 'ROLE_ADMIN' || role.code === 'ROLE_SECTOR') {
        this.rolesWanaka.push(role);
        if (this.edited && setRoles.has(role.code)) {
          this.roleWanaka.id = role.id;
        }
      } else {
        this.rolesOrg.push(role);
        if (this.edited && setRoles.has(role.code)) {
          this.roleOrg.id = role.id;
        }
        if (role.code === 'ROLE_FARMER') {
          this.roleFarmerId = role.id;
        }
      }
    }

    // le premier correspond au max de l'utilisateur loggé (/roles sur le serveur ne renvoie que
    // les rôles egal ou en dessous de celui de l'utilisateur actuel)
    // si admin ou secteur : peut rajouter tous les types de role org
    if (!this.isAdmin && !this.isSector) {
      this.rolesOrg.shift();
    }
  }

  handleOrganisations(organisations: Organisation[]) {
    this.organisations = organisations;
    this.filteredOrganisations = organisations;
    if (!this.edited && this.organisations && this.organisations.length === 1) {
      this.user.organisation = this.organisations[0];
      this.organisationInput = this.user.organisation.name;
      this.getAdvisors(this.user.organisation.id);
    } else if (this.user.organisation && this.user.organisation.name) {
      this.organisationInput = this.user.organisation.name;
    }
    this.organisationIndex = new Map<number, Organisation>();
    for (const organisation of organisations) {
      this.organisationIndex.set(organisation.id, organisation);
    }
  }

  getAdvisors(organisationId: any, resetAdvisor = false) {
    this.userService.getUsers(organisationId, ['ROLE_ADVISOR', 'ROLE_MANAGER']).subscribe((users) => {
      // filtrer l'utilisateur en cours d'édition
      this.advisors = [];
      this.managers = [];
      for (const user of users) {
        if (this.edited && user.id === this.userId) {
          continue;
        }

        if (user.roles.some((r) => r.code === 'ROLE_ADVISOR')) {
          // ses supérieurs
          this.advisors.push(user);
        } else if (user.roles.some((r) => r.code === 'ROLE_MANAGER')) {
          this.managers.push(user);
        }
      }

      if (resetAdvisor) {
        this.user.advisor.id = null;
      }

      this.updateDisplayAdvisors(this.roleOrg.id, resetAdvisor);
    });
  }

  save() {
    // le binding de Angular transform null en 'null'
    const nullValue: any = 'null';

    // Build payload without mutating the form-bound model. The auth
    // interceptor wraps the request in an async token refresh, so the
    // body is serialized after this function returns — mutating
    // this.user and restoring it synchronously below the subscribe()
    // call no longer works (Angular 17 / HttpClient).
    const payload: User = { ...this.user };

    if (!this.isUserMe) {
      const roles: Role[] = [];
      // FIXME beuuuh test de la chaine 'null' faire mieux ???
      const roleWanakaId: any = this.roleWanaka.id;
      if (this.roleWanaka.id && roleWanakaId !== nullValue) {
        roles.push(this.roleWanaka);
      }
      const roleOrgId: any = this.roleOrg.id;
      if (this.roleOrg.id && roleOrgId !== nullValue) {
        roles.push(this.roleOrg);
      }
      payload.roles = roles;
    }

    if (this.isOrgDisabled) {
      payload.organisation = null;
      payload.advisor = null;
    } else if (this.isAdvisorDisabled) {
      payload.advisor = null;
    } else if (payload.advisor && (!payload.advisor.id || payload.advisor.id === nullValue)) {
      payload.advisor = null;
    }

    if (this.phone) {
      let prefix = '';
      if (this.prefixPhone) {
        prefix = this.findIndex(this.countryIndex, this.prefixPhone).telCountryCode;
      }
      payload.phone = prefix + this.phone;
    }
    if (this.mobile) {
      let prefix = '';
      if (this.prefixPhone) {
        prefix = this.findIndex(this.countryIndex, this.prefixMobile).telCountryCode;
      }
      payload.mobile = prefix + this.mobile;
    }

    this.userService.save(payload).subscribe(
      (user) => {
        this.goBack(user);
      },
      (error) => {
        this.handleSaveError(error);
      },
    );
  }

  ngOnInit() {
    this.isAdmin = KeycloakService.auth.authz.hasRealmRole('ROLE_ADMIN');
    this.isSector = KeycloakService.auth.authz.hasRealmRole('ROLE_SECTOR');

    this.i18nInfo = this.i18nService.i18nInfo;

    this.roleWanaka = new Role();
    this.roleWanaka.id = null;
    this.roleOrg = new Role();
    this.roleOrg.id = null;

    const obs1 = this.listService.getCountries(true);
    const obs2 = this.listService.getLanguages();
    forkJoin([obs1, obs2]).subscribe((data: Array<any>) => {
      this.handleCountries(data[0]);
      this.handleLanguages(data[1]);

      for (const c of this.countries) {
        c.languagesGui = c.languagesGui.sort((a: any, b: any) =>
          this.languagesIndex.get(a['id']).label.localeCompare(this.languagesIndex.get(b['id']).label),
        );
        c.languagesPdf = c.languagesPdf.sort((a: any, b: any) =>
          this.languagesIndex.get(a['id']).label.localeCompare(this.languagesIndex.get(b['id']).label),
        );
      }

      // besoin des pays pour l'indicatif donc fait après après la récupération des pays
      this.sub = this.route.params.subscribe((params) => {
        if (this.route.snapshot.url[1].path === 'edit') {
          this.userId = +params['userId'];
          this.edited = true;
          this.userService.getUser(this.userId).subscribe(
            (user) => {
              this.user = user;
              if (user.phone) {
                // TODO mettre un champ prefix dans la DB
                const [_prefixPhone, phone] = this.splitPrefix(user.phone);
                this.phone = phone;
                if (_prefixPhone) {
                  this.prefixPhone = _prefixPhone.id;
                } else {
                  this.prefixPhone = null;
                }
              } else {
                this.prefixPhone = null;
              }
              if (user.mobile) {
                const [_prefixPhone, phone] = this.splitPrefix(user.mobile);
                this.mobile = phone;
                if (_prefixPhone) {
                  this.prefixMobile = _prefixPhone.id;
                } else {
                  this.prefixMobile = null;
                }
              } else {
                this.prefixMobile = null;
              }
              if (!user.country) {
                user.country = new Country();
                user.country.id = null;
              }
              this.isUserMe = KeycloakService.auth.authz.tokenParsed.preferred_username === user.login.toLowerCase();

              this.setupUser();
            },
            (error) => {
              this.handleGetError(error);
            },
          );
        } else if (this.route.snapshot.url[1].path === 'new') {
          this.edited = false;
          this.user = new User();
          this.user.country = new Country();
          this.user.country.id = null;
          this.user.organisation = new Organisation();
          this.user.organisation.id = null;
          this.user.advisor = new User();
          this.user.advisor.id = null;

          this.setupUser();
        }
      });
    });
  }

  splitPrefix(phone: string): any {
    if (phone.startsWith('+')) {
      //+<2 chiffres>
      let _prefixPhone = this.indicatifIndex.get(phone.substring(0, 3));
      if (_prefixPhone) {
        const justPhone = phone.substring(3);
        return [_prefixPhone, justPhone];
      }
      //+<3 chiffres> : pas de collision avec 2 chiffres (dans le système des indicatifs)
      _prefixPhone = this.indicatifIndex.get(phone.substring(0, 4));
      if (_prefixPhone) {
        const justPhone = phone.substring(4);
        return [_prefixPhone, justPhone];
      }
      return [null, phone];
    }
    return [null, phone];
  }

  setupUser() {
    const obs1 = this.roleService.getRoles();
    const obs2 = this.organisationService.getOrganisations();
    forkJoin([obs1, obs2]).subscribe((data: Array<any>) => {
      this.handleRoles(this.edited ? this.user : null, data[0]);
      this.handleOrganisations(data[1]);

      // TODO mieux gérer les propriétés optionnelles
      if (this.user.organisation) {
        this.isOrgDisabled = false;
        this.getAdvisors(this.user.organisation.id);
        if (!this.user.advisor) {
          this.user.advisor = new User();
          this.user.advisor.id = null;
        }
      } else {
        // l'utilisateur ne fait pas obligatoirement parti d'une org
        this.user.organisation = new Organisation();
        this.user.organisation.id = null;
        this.user.advisor = new User();
        this.user.advisor.id = null;
      }

      if (this.user.organisation && this.user.organisation.id) {
        const organisation = this.findIndex(this.organisationIndex, this.user.organisation.id);
        this.organisationCountry = this.findIndex(this.countryIndex, organisation.country.id);
      }
    });
  }

  setSupervisorLabelAndList(roles: Role[]): any {
    if (roles) {
      roles.forEach((r) => {
        if (r.code === 'ROLE_FARMER') {
          // superviseur d'un agri est un conseiller
          this.managerLabel = this.i18nTranslator.translate('user.edit.manager.farmer');
          this.np1 = this.advisors;
        }
        if (r.code === 'ROLE_ADVISOR') {
          // son boss est responsable
          this.managerLabel = this.i18nTranslator.translate('user.edit.manager.conseiller');
          this.np1 = this.managers;
        }
        // sinon pas de responsable donc pas affiché
      });
    }
  }

  handleSaveError(error: any) {
    if (error.status === 401) {
      showErrorMessage(this.modal, this.i18nTranslator.translate('user.edit.unauthorized'));
    } else {
      // FIXME détailler les erreurs du formulaire
      console.error(error);
      // HttpClient stores the parsed body in error.error (HttpErrorResponse),
      // not error.json() like the deprecated @angular/http.
      const body = error?.error ?? error;
      if (body?.errorCode === 'invalid' && body?.data?.user === 'exists_in_db') {
        showErrorMessage(this.modal, this.i18nTranslator.translate('user.edit.error.exists_in_db'));
      } else if (body?.errorCode === 'invalid' && body?.data?.user === 'exists_in_keycloak') {
        showErrorMessage(this.modal, this.i18nTranslator.translate('user.edit.error.exists_in_kc'));
      } else {
        showErrorMessage(this.modal, this.i18nTranslator.translate('edit.error.generic'));
      }
    }
  }

  handleGetError(error: any) {
    if (error.status === 401) {
      showErrorMessage(this.modal, this.i18nTranslator.translate('user.edit.view.unauthorized')).then((r) => {
        this.router.navigate(['/users']);
      });
    } else {
      console.error(error);
      showErrorMessage(this.modal, this.i18nTranslator.translate('error.generic')).then((r) => {
        this.router.navigate(['/users']);
      });
    }
  }

  ngOnDestroy() {
    this.sub.unsubscribe();
  }

  onChangeOrganisation(organisationId: number) {
    organisationId = +organisationId;

    this.getAdvisors(organisationId, true);
    console.log(organisationId);

    const organisation = this.findIndex(this.organisationIndex, organisationId);
    this.organisationCountry = this.findIndex(this.countryIndex, organisation.country.id);

    // reset les langues
    this.user.languageGui = null;
    this.user.languagePdf = null;
  }

  onChangeOrganisationRole(roleId: any) {
    roleId = +roleId;
    this.isOrgDisabled = !roleId;
    // reset (liste a changé)
    this.user.advisor.id = null;
    this.updateDisplayAdvisors(roleId, true);
  }

  onChangeCountry(countryId: number) {
    // on change l'indicatif si on change le pays
    this.prefixPhone = +countryId;
    this.prefixMobile = +countryId;
  }

  // A user's languages can only be changed when the edited user's organisation has
  // internationalisation enabled (the backend ignores them otherwise); without it the
  // organisation defaults are shown read-only. Not the logged-in user's organisation:
  // an admin edits users of every organisation.
  canEditLanguage(): boolean {
    const organisationId = this.user?.organisation?.id;
    if (!organisationId || !this.organisationIndex) {
      return false;
    }
    return !!this.findIndex(this.organisationIndex, organisationId)?.hasI18n;
  }

  onEditLanguageGui() {
    // le champs languageGui ne sera pas nul
    // TODO ajouter bouton cancel ?
    this.user.languageGui = new Language();
    const organisation = this.findIndex(this.organisationIndex, this.user.organisation.id);
    this.user.languageGui.id = organisation.defaultLanguageGui.id;
  }

  onEditLanguagePdf() {
    this.user.languagePdf = new Language();
    const organisation = this.findIndex(this.organisationIndex, this.user.organisation.id);
    this.user.languagePdf.id = organisation.defaultLanguagePdf.id;
  }

  updateDisplayAdvisors(roleId: any, resetAdvisor = false) {
    roleId = +roleId;
    const role = this.allRoles.find((x) => x.id === roleId);
    const roles: Role[] = [];
    if (role) {
      roles.push(role);
    }
    this.setSupervisorLabelAndList(roles);

    this.filteredAdvisors = this.np1;

    if (resetAdvisor && this.np1 && this.np1.length === 1) {
      this.user.advisor.id = this.np1[0].id;
      this.advisorInput = this.np1[0].firstName + ' ' + this.np1[0].lastName;
    } else if (this.user.advisor && this.user.advisor.id) {
      const selected = this.np1.find((a) => a.id === this.user.advisor.id);
      if (selected) {
        this.advisorInput = selected.firstName + ' ' + selected.lastName;
      }
    } else {
      this.advisorInput = '';
    }

    this.isAdvisorDisabled = !(role && (role.code === 'ROLE_ADVISOR' || role.code === 'ROLE_FARMER') && this.advisors);
  }

  onAdvisorInputChange(event: any) {
    const value = event.target.value.toLowerCase();
    this.user.advisor.id = null;
    if (value) {
      this.filteredAdvisors = this.np1.filter((a) => (a.firstName + ' ' + a.lastName).toLowerCase().includes(value));
    } else {
      this.filteredAdvisors = this.np1;
    }
    this.showAdvisorDropdown =
      this.filteredAdvisors.length > 0 || (this.roleOrg && +this.roleOrg.id != this.roleFarmerId);
  }

  selectAdvisor(advisor: User) {
    if (advisor) {
      this.advisorInput = advisor.firstName + ' ' + advisor.lastName;
      this.user.advisor.id = advisor.id;
    } else {
      this.advisorInput = '';
      this.user.advisor.id = null;
    }
    this.showAdvisorDropdown = false;
  }

  hideAdvisorDropdown() {
    setTimeout(() => {
      this.showAdvisorDropdown = false;
    }, 200);
  }

  onOrgaInputChange(event: any) {
    const value = event.target.value.toLowerCase();
    this.user.organisation.id = null;
    if (value) {
      this.filteredOrganisations = this.organisations.filter((org) => org.name.toLowerCase().includes(value));
    } else {
      this.filteredOrganisations = this.organisations;
    }
    this.showOrgaDropdown = this.filteredOrganisations.length > 0;
  }

  selectOrganisation(org: Organisation) {
    this.organisationInput = org.name;
    this.user.organisation.id = org.id;
    this.showOrgaDropdown = false;
    this.onChangeOrganisation(org.id);
  }

  hideOrgaDropdown() {
    setTimeout(() => {
      this.showOrgaDropdown = false;
    }, 200);
  }

  findIndex(index: any, itemId: any) {
    itemId = +itemId;
    return index.get(itemId);
  }
}
